import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Menu, X, LayoutDashboard, Building2, Users, User2, Briefcase, Clock, BarChart3, Shirt, PackageSearch, Settings, UserCog, LogOut, Cog, Zap, Book, FileText, Building, MapPin, Home, Truck, Bed, Bath, Calendar, AlertTriangle, Bug, CheckSquare, AlertCircle } from 'lucide-react';
import { useAuth, MODULES } from '@/contexts/AuthContext';
import logo from '@/images/housyLogo.png';
import ThemeToggle from '@/components/ui/ThemeToggle';
import UpdateNotifier from '@/components/UpdateNotifier';
import { fetchLatestVersion, getLocalVersion } from '@/services/version.service';
import WashingMachineIcon from '@/components/icons/WashingMachineIcon';

const Navbar = () => {
  const { user, logout, hasPermission } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const [appVersion, setAppVersion] = useState(null);
  const [versionData, setVersionData] = useState(null);
  const [openVersionModal, setOpenVersionModal] = useState(false);

  const toggleMenu = () => setIsOpen(!isOpen);

  const activeLinkClass = "bg-primary/20 text-primary";
  const inactiveLinkClass = "hover:bg-primary/10 hover:text-primary/80";

  useEffect(() => {
    let mounted = true;
    (async () => {
      const v = await fetchLatestVersion();
      if (!mounted) return;
      setAppVersion(v?.version || getLocalVersion() || null);
    })();
    return () => { mounted = false; };
  }, []);

  // Definir los ítems de navegación organizados por categorías
  const navItems = [
    // === MÓDULOS PRINCIPALES ===
    {
      name: 'Reportes',
      path: '/dashboard',
      icon: <LayoutDashboard className="h-5 w-5" />,
      module: MODULES.DASHBOARD,
      category: 'core'
    },
 /*    {
      name: 'Reportes',
      path: '/reportes-dashboard',
      icon: <BarChart3 className="h-5 w-5" />,
      module: MODULES.REPORTS,
      category: 'core'
    }, */
    {
      name: 'Portafolios',
      path: '/propiedades',
      icon: <Building2 className="h-5 w-5" />,
      module: MODULES.EDIFICIOS,
      category: 'core'
    },
    
    // === LAVANDERÍA ===
    {
      name: 'Lavandería',
      path: '/lavanderia',
      icon: <WashingMachineIcon className="h-5 w-5" />,
      module: MODULES.LAUNDRY,
      category: 'laundry'
    },
    
    // === CONDUCTOR ===
/*     {
      name: 'Mi Ruta',
      path: '/mi-ruta',
      icon: <Truck className="h-5 w-5" />,
      module: MODULES.CONDUCTOR_RUTA, // Solo conductores pueden ver su ruta
      category: 'conductor'
    }, */
    {
      name: 'Ruta',
      path: '/ruta',
      icon: <MapPin className="h-5 w-5" />,
      module: MODULES.RUTA_ADMIN, // Admin y conductores
      category: 'conductor'
    },
    {
      name: 'Extra Rutas',
      path: '/extra-rutas',
      icon: <AlertTriangle className="h-5 w-5" />,
      module: MODULES.EXTRA_RUTAS_ADMIN, // Solo admin
      category: 'conductor'
    },
    
    // === SOLICITUDES DEL DÍA ===
    {
      name: 'Solicitudes',
      path: '/solicitudes-del-dia',
      icon: <Calendar className="h-5 w-5" />,
      module: MODULES.EDIFICIOS, // Coordinadores pueden ver solicitudes de sus edificios
      category: 'solicitudes'
    },
    
    // === TAREAS ===
/*     {
      name: 'Tareas',
      path: '/solicitudes-desarrollo',
      icon: <CheckSquare className="h-5 w-5" />,
      module: MODULES.SOLICITUDES,
      category: 'desarrollo'
    }, */
    
    // === PQRs ===
    {
      name: 'PQRs',
      path: '/pqrs',
      icon: <AlertCircle className="h-5 w-5" />,
      module: MODULES.PQRS,
      category: 'pqrs'
    },

    // === ASISTENCIA DE PERSONAL ===
    {
      name: 'Asistencia',
      path: '/asistencia',
      icon: <Clock className="h-5 w-5" />,
      module: MODULES.ATTENDANCE,
      category: 'attendance',
    },

    // === FACTURACIÓN ===
/*     {
      name: 'Facturación',
      path: '/facturacion',
      icon: <BarChart3 className="h-5 w-5" />,
      module: MODULES.BILLING,
      category: 'billing',
      subLinks: [
         { 
          name: 'Liquidación', 
          path: '/liquidacion', 
          icon: BarChart3, 
          module: MODULES.BILLING_GENERATE,
          description: 'Generar liquidaciones'
        },
        { 
          name: 'Facturación', 
          path: '/facturacion', 
          icon: BarChart3, 
          module: MODULES.BILLING,
          description: 'Gestionar facturas'
        },
      ].filter(item => hasPermission(item.module))
    }, */
    
    // === DOCUMENTACIÓN ===
/*     {
      name: 'Documentación',
      path: '/documentacion',
      icon: <Book className="h-5 w-5" />,
      module: MODULES.DOCUMENTATION,
      category: 'documentation',
      subLinks: [
        { 
          name: 'Empleados', 
          path: '/documentacion/empleados', 
          icon: Users, 
          module: MODULES.DOCUMENTATION,
          description: 'Documentos de empleados'
        },
        { 
          name: 'Centros', 
          path: '/documentacion/centros', 
          icon: Building2, 
          module: MODULES.DOCUMENTATION,
          description: 'Documentos de centros'
        },
      ].filter(item => hasPermission(item.module))
    }, */
    
    // === CONFIGURACIÓN (PARAMETRIZACIÓN) ===
    {
      name: 'Parametrización',
      path: '/parametrizacion',
      icon: <Settings className="h-5 w-5" />,
      category: 'configuration',
      subLinks: [
        // Gestión de Personas
        {
          name: 'Empleados',
          path: '/parametrizacion/empleados',
          icon: Users,
          module: MODULES.EMPLOYEES,
          category: 'people',
          description: 'Gestionar empleados'
        },
        // Documentación
        {
          name: 'Tipos de Archivo',
          path: '/parametrizacion/tipos-archivo',
          icon: FileText,
          module: MODULES.DOCUMENT_TYPES,
          category: 'documentation',
          description: 'Configurar tipos de documentos'
        },
        {
          name: 'Proveedores',
          path: '/parametrizacion/proveedor',
          icon: Users,
          module: MODULES.PROVEEDOR,
          category: 'people',
          description: 'Gestionar proveedores'
        },
        // Consumibles
        {
          name: 'Insumos',
          path: '/parametrizacion/insumos',
          icon: PackageSearch,
          module: MODULES.INSUMOS,
          category: 'products',
          description: 'Gestionar insumos'
        },

        // Productos y Tipos
        {
          name: 'Tipos de Prenda',
          path: '/parametrizacion/tipos-prenda',
          icon: Shirt,
          module: MODULES.GARMENT_TYPES,
          category: 'products',
          description: 'Gestionar tipos de prendas'
        },
        {
          name: 'Camas',
          path: '/parametrizacion/camas',
          icon: Bed,
          module: MODULES.CAMAS,
          category: 'products',
          description: 'Gestionar tipos de camas y prendas'
        },
        {
          name: 'Baños',
          path: '/parametrizacion/banos',
          icon: Bath,
          module: MODULES.CAMAS,
          category: 'products',
          description: 'Gestionar tipos de baños y prendas'
        },
        {
          name: 'Activos',
          path: '/parametrizacion/hospedajes',
          icon: Home,
          module: MODULES.HOSPEDAJES_ADMIN,
          category: 'infrastructure',
          description: 'Parametrizar activos'
        },
        {
          name: 'Edificios',
          path: '/parametrizacion/edificios',
          icon: Building2,
          module: MODULES.EDIFICIOS_ADMIN,
          category: 'infrastructure',
          description: 'Parametrizar edificios'
        },
        {
          name: 'Zonas',
          path: '/parametrizacion/zonas',
          icon: MapPin,
          module: MODULES.ZONAS,
          category: 'infrastructure',
          description: 'Gestionar zonas geográficas'
        },
        {
          name: 'Usuarios',
          path: '/parametrizacion/usuarios',
          icon: UserCog,
          module: MODULES.USERS,
          category: 'people',
          description: 'Gestionar usuarios del sistema'
        },
      ].filter(item => hasPermission(item.module))
    },
  ].filter(item => {
    // Filtrar ítems principales
    if (item.subLinks) {
      // Mantener el ítem si tiene subLinks y al menos uno es visible
      return item.subLinks.length > 0;
    }
    // Mantener el ítem si no requiere módulo o el usuario tiene permiso
    return !item.module || hasPermission(item.module);
  });



  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: 'spring', stiffness: 120, damping: 20 }}
      className="bg-card shadow-lg sticky top-0 z-50"
    >
      {/* Notificador de actualizaciones: verifica nuevas versiones y muestra toast */}
      <UpdateNotifier checkIntervalMs={60000} />
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex space-x-2 pt-0 ">
            <motion.div whileHover={{ rotate: [0, 5, -5, 0] }}>
              <img src={logo} alt="LOGO-PROFLUX-ERP" className="h-10 w-auto brightness-0" />
            </motion.div>
          </Link>

          <div className="hidden md:flex items-center space-x-1 lg:space-x-2">
            {navItems.map((item) =>
              item.subLinks ? (
                <DropdownMenu key={item.name}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className={`flex items-center space-x-2 px-3 py-2 text-base font-medium transition-colors duration-300 rounded-md ${location.pathname.startsWith(item.path)
                          ? activeLinkClass
                          : inactiveLinkClass
                        }`}
                    >
                      {item.icon}
                      <span>{item.name}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    {item.subLinks.map((sub) => (
                      <DropdownMenuItem key={sub.name} asChild>
                        <NavLink
                          to={sub.path}
                          className={({ isActive }) =>
                            `w-full flex items-center space-x-2 px-3 py-2 text-base font-medium transition-colors duration-300 rounded-md ${isActive ? activeLinkClass : 'hover:bg-primary/10'
                            }`
                          }
                        >
                          <sub.icon className="h-5 w-5" />
                          <span>{sub.name}</span>
                        </NavLink>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <NavLink
                  key={item.name}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center space-x-2 px-3 py-2 text-base font-medium transition-colors duration-300 rounded-md ${isActive ? activeLinkClass : inactiveLinkClass
                    }`
                  }
                >
                  {item.icon}
                  <span>{item.name}</span>
                </NavLink>
              )
            )}
            {/* Version badge */}
            <button
              type="button"
              onClick={() => setOpenVersionModal(true)}
              className="ml-2 px-2 py-1 text-xs rounded bg-primary/10 text-primary select-none hover:bg-primary/20 transition"
              title="Ver cambios de la versión"
            >
              v{appVersion || '—'}
            </button>

            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="ml-2 rounded-full w-10 h-10 p-0">
                    <span className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium">
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                    <span className="sr-only">Menú de usuario</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5 text-sm font-medium">
                    <div className="text-foreground">{user.name} - {user.role}</div>
                    <div className="text-xs text-muted-foreground">{user.email}</div>
                    <div className="text-xs text-primary">{user?.lavanderia?.nombre || ''}</div>
                  </div>
                  
                  {/* Theme toggle (desktop) */}
                  <div className="ml-2">
                    <ThemeToggle variant="buttons" />
                  </div>
                  <DropdownMenuItem asChild>
                    <button
                      onClick={() => logout()}
                      className="w-full flex items-center text-red-600 hover:text-red-700"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Cerrar sesión</span>
                    </button>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <div className="md:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMenu}
              className="text-foreground hover:text-primary"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              <span className="sr-only">Toggle menu</span>
            </Button>
          </div>
        </div>
      </div>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="md:hidden absolute top-20 left-0 right-0 bg-card shadow-lg rounded-b-lg p-4 space-y-2 max-h-[calc(100vh-5rem)] overflow-y-auto"
        >
          {/* Theme toggle (mobile) */}
          <div className="flex justify-end pb-2">
            <ThemeToggle variant="select" />
          </div>
          {navItems.map((item) => (
            <React.Fragment key={item.name}>
              <NavLink
                to={item.subLinks ? '#' : item.path}
                onClick={() => !item.subLinks && setIsOpen(false)}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-md text-base font-medium transition-colors duration-300 flex items-center space-x-3 ${isActive && !item.subLinks ? activeLinkClass : inactiveLinkClass
                  }`
                }
              >
                {item.icon}
                <span>{item.name}</span>
              </NavLink>
              {item.subLinks && (
                <div className="pl-4">
                  {item.subLinks.map((sub) => (
                    <NavLink
                      key={sub.name}
                      to={sub.path}
                      onClick={() => setIsOpen(false)}
                      className={({ isActive }) =>
                        `block px-4 py-2 rounded-md text-base font-medium transition-colors duration-300 flex items-center space-x-3 ${isActive ? activeLinkClass : inactiveLinkClass
                        }`
                      }
                    >
                      <sub.icon className="h-5 w-5" />
                      <span>{sub.name}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </React.Fragment>
          ))}

          {user && (
            <div className="border-t border-border pt-4 mt-4">
              <div className="px-4 py-2 text-sm font-medium text-foreground">
                {user.name}
              </div>
              <div className="px-4 text-xs text-muted-foreground mb-3">
                {user.email}
              </div>
              
                {/* Theme toggle (desktop) */}
                <div className="ml-2">
                  <ThemeToggle variant="buttons" />
                </div>
              <button
                onClick={() => {
                  logout();
                  setIsOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-md flex items-center space-x-2"
              >
                <LogOut className="h-4 w-4" />
                <span>Cerrar sesión</span>
              </button>
            </div>
          )}
        </motion.div>
      )}
      {/* Version Changelog Modal - top-level in Navbar */}
      <Dialog open={openVersionModal} onOpenChange={setOpenVersionModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Versión {appVersion || (versionData?.version ?? '')}</DialogTitle>
            <DialogDescription>
              Novedades y cambios incluidos en esta versión.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {versionData?.changes?.length ? (
              <ul className="list-disc pl-5 space-y-1">
                {versionData.changes.map((c, idx) => (
                  <li key={idx} className="text-sm">{c}</li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No hay cambios listados.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpenVersionModal(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </motion.nav>
  );
};

export default Navbar;